import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { AuthService } from '../../services/auth/auth.service';
import { HttpService } from '../../../../core/services/http/http.service';
import { ApiMethod } from '../../../../core/services/const';
import { GlobalSpinnerService } from 'src/app/core/services/loader/global-spinner.service';
import { ToastrService } from 'ngx-toastr';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { XdsButtonComponent, XdsIconButtonComponent, XdsBadgeComponent, XdsSpinnerComponent, XdsBannerComponent } from '../../../../shared/ui';
import { companyInvitationErrorText } from '../../models/auth-error-text';

interface InvitationInfo {
  Found: boolean;
  IsValid: boolean;
  Email?: string;
  FirstName?: string;
  LastName?: string;
  CompanyName?: string;
  CompanyId?: number;
  IsTeamLeader?: boolean;
  Status?: string;
  ErrorCode?: string;
  ErrorMessage?: string;
}

interface AcceptInvitationResult {
  Success: boolean;
  ErrorCode?: string;
  Message?: string;
  CompanyName?: string;
  CompanyId?: number;
  RequiresLogin?: boolean;
  /** Nur bei `INVITATION_EXPIRED`: `TimeElapsed` | `Superseded` | `Rejected` (BE-12 P3). */
  ExpiredReason?: string;
}

@Component({
  selector: 'app-accept-company-invitation',
  templateUrl: './accept-company-invitation.component.html',
  styleUrls: ['./accept-company-invitation.component.css'],
  imports: [CommonModule, TranslateModule, XdsButtonComponent, XdsIconButtonComponent, XdsBadgeComponent, XdsSpinnerComponent, XdsBannerComponent],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class AcceptCompanyInvitationComponent implements OnInit {
  invitationId: string = '';
  invitationInfo: InvitationInfo | null = null;

  isLoading = true;
  isAccepting = false;
  errorMessage: string = '';
  successMessage: string = '';

  isLoggedIn = false;
  currentUserEmail: string = '';

  /** Fokus auf die Fehlermeldung, sobald sie erscheint (WCAG 2.4.3/4.1.3). */
  @ViewChild('errorBanner', { read: ElementRef })
  set errorBanner(ref: ElementRef<HTMLElement> | undefined) {
    ref?.nativeElement.focus();
  }

  // inject() statt Konstruktor-Parameter: im Vitest (esbuild, ohne Decorator-Metadaten) sonst nicht baubar.
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private httpService = inject(HttpService);
  private spinner = inject(GlobalSpinnerService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);
  public themeService = inject(ThemeService);

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }

  ngOnInit(): void {
    // Parse invitation ID from query params
    const iidParam = this.route.snapshot.queryParams['iid'];
    this.invitationId = Array.isArray(iidParam) ? iidParam[0] : iidParam || '';

    if (!this.invitationId) {
      this.errorMessage = this.translate.instant('INVITATION_ID_MISSING');
      this.isLoading = false;
      return;
    }

    // Check if user is logged in
    this.isLoggedIn = this.authService.isAuthenticated();
    if (this.isLoggedIn) {
      // Get email from JWT token payload
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(window.atob(token.split('.')[1]));
          this.currentUserEmail = payload.Email || '';
        } catch (e) {
          this.currentUserEmail = '';
        }
      }
    }

    // Load invitation info
    this.loadInvitationInfo();
  }

  private loadInvitationInfo(): void {
    this.isLoading = true;
    this.spinner.show();

    this.httpService.requestCall(
      `/ApplicationUser/GetInvitationInfo/${this.invitationId}`,
      ApiMethod.GET
    ).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.isLoading = false;

        // Handle both camelCase and PascalCase responses
        this.invitationInfo = {
          Found: res?.Found ?? res?.found ?? false,
          IsValid: res?.IsValid ?? res?.isValid ?? false,
          Email: res?.Email ?? res?.email,
          FirstName: res?.FirstName ?? res?.firstName,
          LastName: res?.LastName ?? res?.lastName,
          CompanyName: res?.CompanyName ?? res?.companyName,
          CompanyId: res?.CompanyId ?? res?.companyId,
          IsTeamLeader: res?.IsTeamLeader ?? res?.isTeamLeader ?? false,
          Status: res?.Status ?? res?.status,
          ErrorCode: res?.ErrorCode ?? res?.errorCode,
          ErrorMessage: res?.ErrorMessage ?? res?.errorMessage
        };

        if (!this.invitationInfo.Found) {
          this.errorMessage = this.translate.instant('INVITATION_NOT_FOUND');
        } else if (!this.invitationInfo.IsValid) {
          // Nur der übersetzte Code, nie ErrorMessage des Servers (B-06).
          this.errorMessage = this.invitationInfo.ErrorCode
            ? this.invitationErrorText(this.invitationInfo.ErrorCode)
            : this.translate.instant('INVITATION_INVALID');
        } else if (this.isLoggedIn && this.currentUserEmail.toLowerCase() !== this.invitationInfo.Email?.toLowerCase()) {
          // User is logged in but with different email (Text ohne eingeladene Adresse, BE-Vertrag)
          this.errorMessage = this.translate.instant('INVITATION_WRONG_USER', {
            currentEmail: this.currentUserEmail
          });
        }
      },
      error: (err) => {
        this.spinner.hide();
        this.isLoading = false;
        this.errorMessage = this.translate.instant('INVITATION_LOAD_ERROR');
        console.error('Error loading invitation info:', err);
      }
    });
  }

  acceptInvitation(): void {
    if (!this.invitationInfo?.IsValid || this.isAccepting) {
      return;
    }

    // If not logged in, redirect to login with return URL
    if (!this.isLoggedIn) {
      const returnUrl = `/accept-company-invitation?iid=${this.invitationId}`;
      this.router.navigate(['/login'], {
        queryParams: {
          returnUrl: returnUrl,
          email: this.invitationInfo.Email
        }
      });
      return;
    }

    this.isAccepting = true;
    this.spinner.show();

    this.httpService.requestCall(
      '/ApplicationUser/AcceptCompanyInvitation',
      ApiMethod.POST,
      { InvitationId: this.invitationId }
    ).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.isAccepting = false;

        const result: AcceptInvitationResult = {
          Success: res?.Success ?? res?.success ?? false,
          ErrorCode: res?.ErrorCode ?? res?.errorCode,
          Message: res?.Message ?? res?.message,
          CompanyName: res?.CompanyName ?? res?.companyName,
          CompanyId: res?.CompanyId ?? res?.companyId,
          RequiresLogin: res?.RequiresLogin ?? res?.requiresLogin ?? false,
          ExpiredReason: res?.ExpiredReason ?? res?.expiredReason
        };

        if (result.Success) {
          const companyName = result.CompanyName || this.invitationInfo?.CompanyName;
          this.successMessage = this.translate.instant('INVITATION_ACCEPTED_SUCCESS', {
            companyName: companyName
          });

          this.toastr.success(
            this.successMessage,
            this.translate.instant('SUCCESS')
          );

          // Log out user and redirect to login with the new company context
          // This is necessary because the current JWT token doesn't reflect the new company access
          setTimeout(() => {
            // Clear current session tokens
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');

            // Redirect to login page with info about the new company
            this.router.navigate(['/login'], {
              queryParams: {
                email: this.invitationInfo?.Email,
                companyAccepted: companyName
              }
            });
          }, 2500);
        } else if (result.RequiresLogin) {
          // Need to login first — ohne Adresse: der Server liefert keine mehr (BE-12 P3).
          const returnUrl = `/accept-company-invitation?iid=${this.invitationId}`;
          this.router.navigate(['/login'], {
            queryParams: { returnUrl: returnUrl }
          });
        } else {
          // Nur der übersetzte Code, nie Message des Servers (B-06).
          this.errorMessage = result.ErrorCode
            ? this.invitationErrorText(result.ErrorCode, result.ExpiredReason)
            : this.translate.instant('INVITATION_ACCEPT_ERROR');
        }
      },
      error: (err) => {
        this.spinner.hide();
        this.isAccepting = false;
        this.errorMessage = this.translate.instant('INVITATION_ACCEPT_ERROR');
        console.error('Error accepting invitation:', err);
      }
    });
  }

  private invitationErrorText(code: string, expiredReason?: string): string {
    return companyInvitationErrorText(this.translate, code, expiredReason);
  }

  goToLogin(): void {
    const returnUrl = `/accept-company-invitation?iid=${this.invitationId}`;
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: returnUrl,
        email: this.invitationInfo?.Email
      }
    });
  }

  getRoleText(): string {
    return this.invitationInfo?.IsTeamLeader
      ? this.translate.instant('ROLE_TEAMLEADER')
      : this.translate.instant('ROLE_EMPLOYEE');
  }
}
